import { CaseReducer, PayloadAction } from '@reduxjs/toolkit'
import { BomItemType } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { RoutingStep } from 'services/APIs/InternalAPI/internal-api.contracts'
import { ProjectState } from 'store/Project/ProjectTypes'
import { bomItemSelector } from '../selectors/bomItemSelector'
import { bomItemActivitiesSelector } from '../selectors/bomItemWorkingStepsSelector'

export const setWorkingStepProductionOrder: CaseReducer<
  ProjectState,
  PayloadAction<{
    bomItemPointer: BomItemPointer
    activityIdToMove: string
    moveAfterId?: string
  }>
> = (state, action): ProjectState => {
  const workingSteps = bomItemActivitiesSelector({
    bomItemPointer: action.payload.bomItemPointer,
  })({ project: state })

  if (!workingSteps) return state

  const workingStepToMove = workingSteps.find(
    (ws) => ws.id === action.payload.activityIdToMove,
  )

  if (!workingStepToMove) return state

  let newWorkingStepsList = workingSteps.filter(
    (ws) => ws.id !== action.payload.activityIdToMove,
  )

  if (!action.payload.moveAfterId) {
    newWorkingStepsList = [workingStepToMove, ...newWorkingStepsList]
  }

  if (action.payload.moveAfterId) {
    const index = newWorkingStepsList.findIndex(
      (ws) => ws.id === action.payload.moveAfterId,
    )
    newWorkingStepsList.splice(index + 1, 0, workingStepToMove)
  }

  const bomItem = bomItemSelector(
    { project: state },
    action.payload.bomItemPointer,
  )

  if (bomItem) {
    switch (bomItem.type) {
      case BomItemType.project:
      case BomItemType.partInstance: {
        bomItem.activities = newWorkingStepsList
        break
      }
      case BomItemType.partType: {
        bomItem.activities = newWorkingStepsList
        if (bomItem.routingHeaderPointer) {
          const routingHeaderWS =
            state.routingHeaders[bomItem.routingHeaderPointer.id]
              ?.routingSteps ?? []

          const workingStepOrder = new Map(
            newWorkingStepsList.map((workingStep, index) => [
              workingStep.primaryWorkingStep,
              index,
            ]),
          )
          const reorderedRoutingSteps = routingHeaderWS
            .filter((routingStep) =>
              workingStepOrder.has(routingStep.workingStepType),
            )
            .sort(
              (left, right) =>
                (workingStepOrder.get(left.workingStepType) ?? 0) -
                (workingStepOrder.get(right.workingStepType) ?? 0),
            )

          let reorderedRoutingStepIndex = 0
          const newRoutingSteps: Array<RoutingStep> = routingHeaderWS.map(
            (routingStep) => {
              if (!workingStepOrder.has(routingStep.workingStepType)) {
                return routingStep
              }

              const reorderedRoutingStep =
                reorderedRoutingSteps[reorderedRoutingStepIndex]
              reorderedRoutingStepIndex += 1
              return reorderedRoutingStep
            },
          )

          state.routingHeaders[bomItem.routingHeaderPointer.id].routingSteps =
            newRoutingSteps
        }
        break
      }
      case BomItemType.assemblyType:
      case BomItemType.assemblyInstance: {
        bomItem.assemblyActivities = newWorkingStepsList
        break
      }
    }
  }

  return state
}
